/**
 * Zod 스키마 — web/admin/worker 세 앱이 공유.
 *
 * - 컨텐츠 frontmatter는 Astro Content Collections / 발행 마크다운 / API 응답에서 동일하게 검증된다.
 * - API 입출력 스키마는 Worker 라우트의 검증 + admin 폼 / 클라이언트 타입 추론에 모두 사용된다.
 * - 새 필드 추가 시 web의 SEO/GEO 노출 컴포넌트 영향도 함께 검토할 것.
 */
import { z } from 'zod';

const SLUG_REGEX = /^[a-z0-9-]+$/;

/**
 * /digest/ 하위 고정 경로와 충돌하는 슬러그 금지 (홈 개편 2026-08-18).
 * 필드 레벨 refine이라 .partial() 파생 스키마(draft·admin 폼)에도 그대로 적용된다.
 * LLM이 슬러그를 생성하므로 발행 전 최종 검증에서 반드시 걸러져야 한다.
 */
// apps/web/src/pages/digest/ 하위에 고정 라우트를 추가하면 여기에도 반드시 추가할 것.
// 'rss.xml'은 SLUG_REGEX(점 불허)만으로도 걸러지지만 규칙 완화에 대비해 남겨둔다.
export const RESERVED_SLUGS = ['archive', 'topics', 'index', 'rss', 'rss.xml'] as const;

// ---------------------------------------------------------------------------
// 도메인 스키마
// ---------------------------------------------------------------------------

export const SourceSchema = z.object({
  title: z.string().min(1),
  url: z.string().url(),
});

export const ContentFrontmatterSchema = z.object({
  title: z.string().min(1).max(120),
  description: z.string().min(1).max(200), // SEO meta description
  slug: z
    .string()
    .regex(SLUG_REGEX)
    .refine((s) => !(RESERVED_SLUGS as readonly string[]).includes(s), {
      message: '예약된 슬러그입니다 (/digest/ 하위 고정 경로와 충돌)',
    }),
  publishedAt: z.string().datetime(),
  updatedAt: z.string().datetime().optional(),
  tags: z.array(z.string()).default([]),
  category: z.string().optional(),
  ogImage: z.string().url().optional(),
  canonicalUrl: z.string().url().optional(),
  /** 검색엔진 색인 제외 (프루닝). robots meta + sitemap 제외. llms.txt에는 유지(GEO). */
  noindex: z.boolean().optional(),

  // GEO 최적화용 (LLM 크롤러 친화)
  summary: z.string().optional(),
  keyPoints: z.array(z.string()).optional(),
  sources: z.array(SourceSchema).optional(),
});

/** 발행 후 마크다운 1건. frontmatter + 본문 */
export const ContentSchema = z.object({
  frontmatter: ContentFrontmatterSchema,
  body: z.string().min(1),
});

export const DraftStatusSchema = z.enum(['draft', 'published']);

/**
 * D1에 저장되는 작성 중인 컨텐츠.
 * 발행 직전까지는 frontmatter가 부분만 채워질 수 있으므로 partial 사용.
 */
export const DraftSchema = z.object({
  id: z.string().min(1),
  status: DraftStatusSchema,
  frontmatter: ContentFrontmatterSchema.partial(),
  body: z.string().default(''),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  generationMeta: z
    .object({
      model: z.string().optional(),
      crawledAt: z.string().datetime().optional(),
      sourceUrls: z.array(z.string().url()).optional(),
    })
    .optional(),
});

// ---------------------------------------------------------------------------
// API 입출력 스키마
// ---------------------------------------------------------------------------

/** POST /api/batch — 크롤링 + LLM 가공 트리거 */
export const BatchCategorySchema = z.enum(['news', 'community']);
export type BatchCategory = z.infer<typeof BatchCategorySchema>;

export const BatchRequestSchema = z
  .object({
    /** 소스 카테고리 필터. 미지정 시 전체. */
    category: BatchCategorySchema.optional(),
    hint: z.string().optional(),
  })
  .strict();

/**
 * 일 LLM 토큰 사용량이 임계값(LLM_DAILY_TOKEN_BUDGET)을 초과했을 때만 응답에 포함.
 * 무료 티어 한도(RPD/TPD) 근접 신호. admin SPA(Phase 4)가 이 필드를 보고 경고 표시.
 */
export const UsageAlertSchema = z.object({
  exceeded: z.literal(true),
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  tokensToday: z.number(),
  budgetTokens: z.number(),
  callsToday: z.number(),
});

/**
 * 5.13: 1 batch → 0~3 draft. LLM이 흥미로운 입력이 없다고 판단하면 빈 배열 반환 가능.
 * 1 draft가 여러 source URL을 종합한 결과일 수도 있음 (sources frontmatter 참고).
 */
export const BatchResponseSchema = z.object({
  draftIds: z.array(z.string()).max(3),
  usageAlert: UsageAlertSchema.optional(),
});

/** GET /api/drafts */
export const DraftListResponseSchema = z.object({
  drafts: z.array(DraftSchema),
});

/** PUT /api/drafts/:id — 부분 업데이트 */
export const DraftUpdateSchema = z
  .object({
    frontmatter: ContentFrontmatterSchema.partial().optional(),
    body: z.string().optional(),
    status: DraftStatusSchema.optional(),
  })
  .strict();

/** POST /api/publish/:id — 마크다운 생성 + GitHub 커밋 */
export const PublishRequestSchema = z
  .object({
    /** 발행 시점에 frontmatter 일부 덮어쓰기 (예: publishedAt 확정) */
    frontmatterOverrides: ContentFrontmatterSchema.partial().optional(),
    /** GitHub 커밋 메시지 오버라이드 (없으면 worker가 기본값 생성) */
    commitMessage: z.string().optional(),
  })
  .strict();

export const PublishResponseSchema = z.object({
  slug: z.string(),
  commitSha: z.string(),
  url: z.string().url().optional(),
});

export const ErrorResponseSchema = z.object({
  error: z.string(),
  details: z.unknown().optional(),
});

/**
 * GET /api/published — GitHub repo `content/posts/`의 발행 글 목록.
 * **데이터 소스는 GitHub repo (단일 진실 공급원)**. D1 published row와 분리되어,
 * D1 row를 정리해도 공개 사이트에 남은 글은 여기 그대로 표시된다.
 */
export const PublishedItemSchema = z.object({
  slug: z.string(),
  title: z.string(),
  publishedAt: z.string().datetime(),
  /** GitHub repo 내 파일 경로 — 삭제 시 사용 */
  path: z.string(),
});

export const PublishedListResponseSchema = z.object({
  items: z.array(PublishedItemSchema),
});

/** DELETE /api/published/:slug — GitHub 마크다운 + (있다면) D1 row까지 삭제 */
export const UnpublishResponseSchema = z.object({
  slug: z.string(),
  commitSha: z.string(),
});

/**
 * POST /api/published/reindex — GitHub repo에서 발행본 목록 인덱스(D1)를 재구축.
 * 목록 API는 이 인덱스를 읽는다. 최초 백필 · 레포 직접 편집 후 정합성 복구용.
 */
export const PublishedReindexResponseSchema = z.object({
  indexed: z.number().int().nonnegative(),
});

export type PublishedItem = z.infer<typeof PublishedItemSchema>;
export type PublishedListResponse = z.infer<typeof PublishedListResponseSchema>;
export type UnpublishResponse = z.infer<typeof UnpublishResponseSchema>;
export type PublishedReindexResponse = z.infer<typeof PublishedReindexResponseSchema>;

/** 발행 글 편집 — 전체 frontmatter + body. GET /api/published/:slug */
export const PublishedPostSchema = z.object({
  slug: z.string(),
  path: z.string(),
  frontmatter: ContentFrontmatterSchema,
  body: z.string(),
});
export type PublishedPost = z.infer<typeof PublishedPostSchema>;

/** 편집 저장 요청. slug는 URL param으로 잠금 → 본문엔 frontmatter+body만. PUT /api/published/:slug */
export const PublishedUpdateSchema = z.object({
  frontmatter: ContentFrontmatterSchema,
  body: z.string().min(1),
});
export type PublishedUpdate = z.infer<typeof PublishedUpdateSchema>;

export const PublishedUpdateResponseSchema = z.object({
  slug: z.string(),
  commitSha: z.string(),
  url: z.string().url(),
});
export type PublishedUpdateResponse = z.infer<typeof PublishedUpdateResponseSchema>;

// ---------------------------------------------------------------------------
// Analytics (5.15)
// ---------------------------------------------------------------------------

/**
 * 조회수 집계 기간. CF 무료 zone analytics는 adaptive groups 데이터셋 보존이 ~7일이라
 * 7d만 의미 있음. 30d는 실제로 7~8일치 partial만 반환되어 토글 제거. plan 업그레이드 또는
 * RUM 데이터셋 전환 시 enum에 다시 추가.
 */
export const AnalyticsRangeSchema = z.enum(['7d']);
export type AnalyticsRange = z.infer<typeof AnalyticsRangeSchema>;

export const AnalyticsViewItemSchema = z.object({
  slug: z.string(),
  views: z.number().int().nonnegative(),
});

/** GET /api/analytics/views?range=7d|30d — Cloudflare Web Analytics RUM 데이터. */
export const AnalyticsViewsResponseSchema = z.object({
  range: AnalyticsRangeSchema,
  items: z.array(AnalyticsViewItemSchema),
});

export type AnalyticsViewItem = z.infer<typeof AnalyticsViewItemSchema>;
export type AnalyticsViewsResponse = z.infer<typeof AnalyticsViewsResponseSchema>;

// ---------------------------------------------------------------------------
// Topic monitoring (트래픽 확대 플랜 — admin /topics 페이지)
// ---------------------------------------------------------------------------

/**
 * GET /api/topics/warnings?threshold=5
 *
 * 발행된 글들의 tags를 스캔해서:
 *   - 매핑 안 된 태그 중 임계(threshold) 이상 누적된 것 → 토픽 승격 후보
 *   - 토픽별 글 수 분포 → 매핑 정합성 시각 확인
 *
 * 토픽 정의는 packages/shared/src/topics.ts (단일 진실 공급원).
 */
export const TopicWarningsUnmappedSchema = z.object({
  tag: z.string(),
  count: z.number().int().nonnegative(),
});

export const TopicWarningsDistributionSchema = z.object({
  slug: z.string(),
  displayName: z.string(),
  count: z.number().int().nonnegative(),
});

export const TopicWarningsResponseSchema = z.object({
  threshold: z.number().int().positive(),
  totalPosts: z.number().int().nonnegative(),
  unmappedTags: z.array(TopicWarningsUnmappedSchema),
  topicDistribution: z.array(TopicWarningsDistributionSchema),
});

export type TopicWarningsResponse = z.infer<typeof TopicWarningsResponseSchema>;
